import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../engine/api/client';
import { useApi } from '../engine/api/context';
import { ICON_NAMES, type IconName } from '../engine/design/icons';
import type { Capabilities, Me, MenuButton } from './types';

// The signed-in customer's context: the /portal/v1/me aggregate (contact, client, role +
// capabilities, branding, settings, menu buttons, counts). Pages read capabilities from here to
// hide what the role cannot do; the API and the DB policies enforce it regardless.

export const ME_KEY = ['portal', 'me'] as const;

export interface PortalCtx {
  me: Me;
  /** Capability check mirroring the server's `capOf`: false / 'none' deny, anything else allows. */
  can: (capability: string) => boolean;
  refresh: () => void;
}

const Ctx = createContext<PortalCtx | null>(null);

export function capAllows(caps: Capabilities | undefined, key: string): boolean {
  const v = caps?.[key];
  if (v === undefined || v === null || v === false || v === 'none') return false;
  return true;
}

export function usePortalMe() {
  const api = useApi();
  return useQuery<Me, ApiError>({ queryKey: ME_KEY, queryFn: () => api.request<Me>('/v1/me'), retry: false, staleTime: 60_000 });
}

export function PortalProvider({ me, children }: { me: Me; children: ReactNode }) {
  const qc = useQueryClient();
  const value = useMemo<PortalCtx>(() => ({ me, can: (k) => capAllows(me.role.capabilities, k), refresh: () => void qc.invalidateQueries({ queryKey: ME_KEY }) }), [me, qc]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePortal(): PortalCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('usePortal requires <PortalProvider>');
  return c;
}

/** A tenant-configured icon name, or a sensible fallback when it is not in the icon set. */
export function safeIcon(name: string | null | undefined, fallback: IconName = 'circle'): IconName {
  return name && (ICON_NAMES as string[]).includes(name) ? (name as IconName) : fallback;
}

/** Menu buttons carry app-relative routes ('/tickets'); the SPA mounts the portal at /portal. */
export function buttonHref(b: MenuButton): string {
  if (/^https?:\/\//.test(b.route)) return b.route;
  return `/portal${b.route.startsWith('/') ? b.route : `/${b.route}`}`.replace(/\/portal\/$/, '/portal');
}

export function isExternal(b: MenuButton): boolean {
  return /^https?:\/\//.test(b.route);
}
